/**
 * GraphDashboard — 3 New Full-Stack Tabs
 * Tab 6: Industry Matrices   -> GET/POST /api/v1/graph/industries/*
 * Tab 7: Reinforcement Loops -> GET /api/v1/graph/reinforcement-loops/*
 * Tab 8: Cascade Simulator   -> POST /api/v1/graph/cascade-simulator/*
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  useFetch, useMutation, useGraphWS, useModuleNav,
  apiFetch, LoadingState, ErrorState, EmptyState, WSIndicator,
} from "../../../services/api_service";

const C = {
  bg: "#050a14", surface: "#0c1220", card: "#111c30",
  border: "#1a2744", text: "#e2e8f0", muted: "#6b7a96", dim: "#384660",
  blue: "#3b82f6", amber: "#f59e0b", emerald: "#10b981",
  purple: "#8b5cf6", cyan: "#06b6d4", red: "#ef4444",
  teal: "#14b8a6", orange: "#f97316", lime: "#84cc16",
  indigo: "#6366f1", white: "#fff",
};

const ESRS_NAMES = {
  E1:"Climate Change",E2:"Pollution",E3:"Water & Marine",E4:"Biodiversity",
  E5:"Circular Economy",S1:"Own Workforce",S2:"Value Chain Workers",
  S3:"Communities",S4:"Consumers",G1:"Business Conduct",
};
const ESRS_ICONS = {
  E1:"\u{1F321}\uFE0F",E2:"\u{1F3ED}",E3:"\u{1F4A7}",E4:"\u{1F33F}",E5:"\u267B\uFE0F",
  S1:"\u{1F477}",S2:"\u{1F517}",S3:"\u{1F3D8}\uFE0F",S4:"\u{1F6D2}",G1:"\u2696\uFE0F",
};
const SEV_COLOR = { 5: C.red, 4: C.orange, 3: C.amber, 2: C.lime, 1: C.emerald };
const THRESHOLD = 8;

const Pl = ({ color: cl, children, small, onClick, active, glow }) => (
  <span onClick={onClick} data-testid={typeof children === 'string' ? `pill-${children.toLowerCase().replace(/\s/g,'-')}` : undefined}
    style={{ display:"inline-flex",alignItems:"center",gap:3,padding:small?"1px 6px":"3px 9px",borderRadius:99,fontSize:small?8:10,fontWeight:600,background:active?cl+"22":cl+"10",color:cl,border:`1px solid ${active?cl+"66":cl+"22"}`,cursor:onClick?"pointer":"default",transition:"all .2s",boxShadow:glow?`0 0 6px ${cl}33`:"none" }}>{children}</span>
);
const Bar = ({ v, mx, cl, h=5 }) => (
  <div style={{ width:"100%",height:h,borderRadius:99,background:C.border,overflow:"hidden" }}>
    <div style={{ width:`${Math.min((v/mx)*100,100)}%`,height:"100%",borderRadius:99,background:`linear-gradient(90deg,${cl}88,${cl})`,transition:"width .4s" }}/>
  </div>
);


// ═══════════════════════════════════════════════════════════════════
// TAB 6: INDUSTRY MATRICES
// ═══════════════════════════════════════════════════════════════════

export function IndustryMatricesTab() {
  const [view, setView] = useState("select");
  const [selectedInd, setSelectedInd] = useState(null);
  const [expandedTopic, setExpandedTopic] = useState(null);
  const nav = useModuleNav();

  const { data: indData, loading: indLoading, error: indError, refetch: refetchInd } = useFetch("/graph/industries");

  const [matrix, setMatrix] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixError, setMatrixError] = useState(null);

  const [applyLoading, setApplyLoading] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [applyError, setApplyError] = useState(null);

  const [deviations, setDeviations] = useState(null);
  const [compData, setCompData] = useState(null);
  const [compLoading, setCompLoading] = useState(false);

  const { lastEvent, connected } = useGraphWS();
  useEffect(() => {
    if (lastEvent?.type === "cascade.completed") refetchInd();
  }, [lastEvent, refetchInd]);

  useEffect(() => {
    if (!selectedInd) { setMatrix(null); return; }
    setMatrixLoading(true); setMatrixError(null);
    apiFetch(`/graph/industries/${selectedInd}/matrix`).then(r => {
      if (r?._error) setMatrixError(r); else setMatrix(r);
    }).finally(() => setMatrixLoading(false));
  }, [selectedInd]);

  useEffect(() => {
    if (!selectedInd || !matrix) { setDeviations(null); return; }
    apiFetch(`/graph/industries/${selectedInd}/deviations`).then(r => {
      if (!r?._error) setDeviations(r);
    });
  }, [selectedInd, matrix]);

  useEffect(() => {
    if (view === "compare" && !compData) {
      setCompLoading(true);
      apiFetch("/graph/industries/comparison").then(r => {
        if (!r?._error) setCompData(r);
      }).finally(() => setCompLoading(false));
    }
  }, [view, compData]);

  const industries = indData?.industries || [];
  const matrixTopics = matrix?.topics || [];
  const indMeta = matrix?.industry || industries.find(i => i.id === selectedInd);
  const materialCount = matrixTopics.filter(t => t.verdict === "material").length;

  const handleApply = async () => {
    setApplyLoading(true); setApplyError(null);
    const r = await apiFetch(`/graph/industries/${selectedInd}/apply`, { method: "POST", body: { confirm: true } });
    if (r?._error) setApplyError(r); else setApplyResult(r);
    setApplyLoading(false);
    if (!r?._error) refetchInd();
  };

  // INDUSTRY SELECTOR
  if (view === "select") {
    if (indLoading) return <LoadingState message="Loading industry templates..." />;
    if (indError) return <ErrorState error={indError} onRetry={refetchInd} />;
    if (!industries.length) return <EmptyState title="No industry templates loaded" subtitle="Industry matrix service may not be running" />;

    return (
      <div>
        <div style={{ textAlign:"center",marginBottom:16 }}>
          <div style={{ fontSize:14,fontWeight:800,color:C.teal }}>Industry Materiality Templates</div>
          <div style={{ fontSize:9,color:C.muted,marginTop:2 }}>{industries.length} industries x 10 ESRS topics. Select to review, adjust, and apply.</div>
        </div>
        <div data-testid="industry-grid" style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6,marginBottom:12 }}>
          {industries.map(ind => (
            <div key={ind.id} data-testid={`industry-card-${ind.id}`} onClick={() => { setSelectedInd(ind.id); setView("matrix"); setExpandedTopic(null); setApplyResult(null); }}
              style={{ padding:"14px 12px",borderRadius:8,background:C.card,border:`1px solid ${ind.color || C.border}22`,cursor:"pointer",transition:"all .2s",textAlign:"center" }}>
              <div style={{ fontSize:11,fontWeight:700,color:C.text }}>{ind.name}</div>
              <div style={{ fontSize:7,color:C.dim,marginTop:1 }}>NACE: {ind.nace || "N/A"}</div>
              <div style={{ marginTop:6,display:"flex",justifyContent:"center",gap:4 }}>
                <Pl color={C.emerald} small>{ind.material_count ?? "?"} material</Pl>
                <Pl color={C.dim} small>{(ind.topic_count || 8) - (ind.material_count ?? 0)} not</Pl>
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign:"center" }}>
          <button data-testid="compare-btn" onClick={() => setView("compare")} style={{ padding:"6px 18px",borderRadius:6,border:`1px solid ${C.cyan}44`,background:C.cyan+"12",color:C.cyan,fontSize:10,fontWeight:700,cursor:"pointer" }}>
            Cross-Industry Comparison Heatmap
          </button>
        </div>
      </div>
    );
  }

  // COMPARISON HEATMAP
  if (view === "compare") {
    if (compLoading) return <LoadingState message="Building cross-industry heatmap..." />;
    const topics = compData?.topics || [];
    const compIndustries = compData?.industries || [];
    return (
      <div>
        <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:10 }}>
          <button onClick={() => setView("select")} style={{ padding:"3px 8px",borderRadius:4,border:`1px solid ${C.border}`,background:"transparent",color:C.muted,fontSize:8,cursor:"pointer" }}>Back</button>
          <span style={{ fontSize:12,fontWeight:700,color:C.cyan }}>Cross-Industry Comparison</span>
        </div>
        {topics.length === 0 ? <EmptyState title="No comparison data" /> : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ borderCollapse:"collapse",width:"100%",fontSize:9 }}>
              <thead><tr>
                <th style={{ padding:"6px 8px",borderBottom:`1px solid ${C.border}`,textAlign:"left",color:C.muted,fontSize:8 }}>Topic</th>
                {compIndustries.map(ind => (
                  <th key={ind} style={{ padding:"6px 4px",borderBottom:`1px solid ${C.border}`,textAlign:"center",color:C.muted,fontSize:7 }}>{ind}</th>
                ))}
              </tr></thead>
              <tbody>
                {topics.map(t => (
                  <tr key={t.topic}>
                    <td style={{ padding:"6px 8px",borderBottom:`1px solid ${C.border}08`,fontWeight:600,color:C.text }}>{t.topic}: {t.label}</td>
                    {compIndustries.map(ind => {
                      const sc = t.scores?.[ind] ?? 0;
                      const cl = sc >= 30 ? C.red : sc >= 18 ? C.amber : sc >= 8 ? C.lime : C.emerald;
                      return <td key={ind} style={{ padding:"4px",borderBottom:`1px solid ${C.border}08`,textAlign:"center" }}>
                        <div style={{ width:42,height:28,borderRadius:4,background:cl+"20",border:`1px solid ${cl}33`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto" }}>
                          <span style={{ fontSize:10,fontWeight:700,color:cl }}>{sc}</span>
                        </div>
                      </td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // SINGLE INDUSTRY MATRIX
  if (matrixLoading) return <LoadingState message={`Loading ${selectedInd} matrix...`} />;
  if (matrixError) return <ErrorState error={matrixError} />;

  return (
    <div>
      <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:10 }}>
        <button onClick={() => { setView("select"); setSelectedInd(null); }} style={{ padding:"3px 8px",borderRadius:4,border:`1px solid ${C.border}`,background:"transparent",color:C.muted,fontSize:8,cursor:"pointer" }}>Back</button>
        <div>
          <div style={{ fontSize:13,fontWeight:800,color:indMeta?.color || C.teal }}>{indMeta?.name || selectedInd}</div>
          <div style={{ fontSize:8,color:C.dim }}>{materialCount}/10 material | Threshold: {THRESHOLD}</div>
        </div>
        <div style={{ flex:1 }} />
        <WSIndicator connected={connected} />
        {!applyResult ? (
          <button data-testid="apply-template-btn" onClick={handleApply} disabled={applyLoading}
            style={{ padding:"6px 16px",borderRadius:6,border:"none",background:applyLoading?C.dim:C.emerald,color:C.bg,fontSize:10,fontWeight:800,cursor:applyLoading?"wait":"pointer" }}>
            {applyLoading ? "Applying..." : "Apply Template to My IROs"}
          </button>
        ) : (
          <Pl color={C.emerald} glow>Applied - {applyResult.iros_updated || materialCount} IROs cascaded</Pl>
        )}
      </div>

      {applyError && <ErrorState error={applyError} />}

      {applyResult && (
        <div style={{ padding:"8px 12px",borderRadius:6,background:C.emerald+"06",border:`1px solid ${C.emerald}22`,marginBottom:8,fontSize:9 }}>
          <span style={{ fontWeight:700,color:C.emerald }}>Template Applied</span> - {applyResult.iros_updated || materialCount} IROs updated. Master switch cascade triggered.
        </div>
      )}

      {/* Score Grid */}
      <div style={{ display:"flex",flexDirection:"column",gap:3 }}>
        <div style={{ display:"grid",gridTemplateColumns:"190px 48px 48px 58px 48px 48px 58px 65px 55px",gap:4,padding:"6px 8px",borderRadius:5,background:C.surface,border:`1px solid ${C.border}` }}>
          {["ESRS Topic","iSev","iLik","Impact","fMag","fLik","Financial","Combined","Verdict"].map(h => (
            <div key={h} style={{ fontSize:7,fontWeight:700,color:C.muted,textAlign:"center" }}>{h}</div>
          ))}
        </div>

        {matrixTopics.map(t => {
          const iScore = (t.impact_score || (t.i_severity || 0) * (t.i_likelihood || 0));
          const fScore = (t.financial_score || (t.f_magnitude || 0) * (t.f_likelihood || 0));
          const combined = iScore + fScore;
          const isMat = t.verdict === "material";
          const isExp = expandedTopic === t.topic;
          const deviation = deviations?.deviations?.find(d => d.topic === t.topic);

          return (
            <div key={t.topic}>
              <div onClick={() => setExpandedTopic(isExp ? null : t.topic)}
                data-testid={`topic-row-${t.topic}`}
                style={{ display:"grid",gridTemplateColumns:"190px 48px 48px 58px 48px 48px 58px 65px 55px",gap:4,padding:"7px 8px",borderRadius:isExp?"5px 5px 0 0":5,background:isExp?C.card+"ee":C.card,border:`1px solid ${isExp?(isMat?C.emerald:C.amber)+"33":isMat?C.emerald+"18":C.border}`,cursor:"pointer",transition:"all .15s" }}>
                <div style={{ display:"flex",alignItems:"center",gap:4,fontSize:10 }}>
                  <span style={{ fontWeight:600,color:C.text }}>{t.topic}: {ESRS_NAMES[t.topic] || t.label}</span>
                  {deviation?.significant && <span style={{ fontSize:7,color:C.amber }}>!</span>}
                </div>
                <div style={{ textAlign:"center",fontSize:10,fontWeight:600,color:SEV_COLOR[t.i_severity || t.impact_score] || C.text }}>{t.i_severity || "-"}</div>
                <div style={{ textAlign:"center",fontSize:10,color:C.text }}>{t.i_likelihood || "-"}</div>
                <div style={{ display:"flex",alignItems:"center",gap:3 }}>
                  <span style={{ fontSize:10,fontWeight:700,color:iScore>=THRESHOLD?C.red:C.muted }}>{iScore}</span>
                  <div style={{ flex:1 }}><Bar v={iScore} mx={25} cl={iScore>=THRESHOLD?C.red:C.muted} h={3}/></div>
                </div>
                <div style={{ textAlign:"center",fontSize:10,fontWeight:600,color:SEV_COLOR[t.f_magnitude || t.financial_score] || C.text }}>{t.f_magnitude || "-"}</div>
                <div style={{ textAlign:"center",fontSize:10,color:C.text }}>{t.f_likelihood || "-"}</div>
                <div style={{ display:"flex",alignItems:"center",gap:3 }}>
                  <span style={{ fontSize:10,fontWeight:700,color:fScore>=THRESHOLD?C.blue:C.muted }}>{fScore}</span>
                  <div style={{ flex:1 }}><Bar v={fScore} mx={25} cl={fScore>=THRESHOLD?C.blue:C.muted} h={3}/></div>
                </div>
                <div style={{ textAlign:"center" }}>
                  <span style={{ fontSize:12,fontWeight:800,color:combined>=30?C.red:combined>=16?C.amber:C.emerald }}>{combined}</span>
                  <span style={{ fontSize:7,color:C.dim }}>/50</span>
                </div>
                <div style={{ display:"flex",justifyContent:"center" }}>
                  <Pl color={isMat?C.emerald:C.dim} small>{isMat?"MAT":"NOT"}</Pl>
                </div>
              </div>

              {isExp && (
                <div style={{ border:`1px solid ${(isMat?C.emerald:C.amber)}33`,borderTop:"none",borderRadius:"0 0 5px 5px",background:C.surface,padding:"12px 14px" }}>
                  {t.rationale && (
                    <div style={{ padding:"6px 10px",borderRadius:5,background:C.card,marginBottom:8,fontSize:9,color:C.text,lineHeight:1.6 }}>
                      <span style={{ fontWeight:700,color:C.cyan }}>Rationale: </span>{t.rationale}
                    </div>
                  )}
                  {t.top_risks?.length > 0 && (
                    <div style={{ marginBottom:8 }}>
                      <div style={{ fontSize:9,fontWeight:700,color:C.red,marginBottom:4 }}>TOP SECTOR RISKS ({t.top_risks.length})</div>
                      {t.top_risks.map((r,i) => (
                        <div key={i} style={{ display:"flex",alignItems:"center",gap:6,padding:"5px 10px",borderRadius:4,background:C.card,marginBottom:2,fontSize:9 }}>
                          <span style={{ color:C.text,flex:1 }}>{r.name || r}</span>
                          {r.severity && <Pl color={SEV_COLOR[r.severity]} small>Sev {r.severity}</Pl>}
                        </div>
                      ))}
                    </div>
                  )}
                  {t.evidence_types?.length > 0 && (
                    <div style={{ marginBottom:8 }}>
                      <div style={{ fontSize:9,fontWeight:700,color:C.teal,marginBottom:4 }}>EXPECTED EVIDENCE TYPES</div>
                      <div style={{ display:"flex",gap:3,flexWrap:"wrap" }}>
                        {t.evidence_types.map((e,i) => <Pl key={i} color={C.teal} small>{e}</Pl>)}
                      </div>
                    </div>
                  )}
                  {t.regulatory_cascade?.length > 0 && (
                    <div style={{ marginBottom:8 }}>
                      <div style={{ fontSize:9,fontWeight:700,color:C.purple,marginBottom:4 }}>REGULATORY CASCADE (when material)</div>
                      {t.regulatory_cascade.map((rc,i) => (
                        <div key={i} style={{ display:"flex",alignItems:"center",gap:6,padding:"4px 8px",borderRadius:4,background:C.card,marginBottom:2,fontSize:8 }}>
                          <Pl color={C.purple} small>{rc.framework}</Pl>
                          <span style={{ color:C.text }}>{rc.requirement || rc.description}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {deviation && (
                    <div style={{ padding:"6px 10px",borderRadius:5,background:C.amber+"06",border:`1px solid ${C.amber}18`,fontSize:9 }}>
                      <span style={{ fontWeight:700,color:C.amber }}>DEVIATION FROM BASELINE: </span>
                      <span style={{ color:C.text }}>{deviation.description || `Score differs by ${deviation.deviation_impact || "?"} impact / ${deviation.deviation_financial || "?"} financial points.`}</span>
                    </div>
                  )}
                  <div style={{ display:"flex",gap:6,marginTop:8,fontSize:8 }}>
                    <span style={{ color:C.blue,cursor:"pointer" }} onClick={() => nav.toMateriality()}>Open in Materiality Intelligence</span>
                    <span style={{ color:C.teal,cursor:"pointer" }} onClick={() => nav.toEvidence()}>View evidence for {t.topic}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// TAB 7: REINFORCEMENT LOOPS
// ═══════════════════════════════════════════════════════════════════

export function ReinforcementLoopsTab() {
  const [selectedLoop, setSelectedLoop] = useState(null);
  const [traceResult, setTraceResult] = useState(null);
  const [tracing, setTracing] = useState(false);
  const nav = useModuleNav();

  const { data: loopData, loading, error, refetch } = useFetch("/graph/reinforcement-loops");

  const { lastEvent, connected } = useGraphWS();
  useEffect(() => {
    if (lastEvent?.type === "cascade.completed" || lastEvent?.type === "materiality.verdict.changed") {
      refetch();
      setTraceResult(null);
    }
  }, [lastEvent, refetch]);

  const loops = loopData?.loops || [];
  const loop = selectedLoop ? loops.find(l => l.id === selectedLoop) : null;

  const runTrace = async (loopId) => {
    setTracing(true); setTraceResult(null);
    const r = await apiFetch(`/graph/reinforcement-loops/${loopId}/trace`);
    if (!r?._error) setTraceResult(r);
    setTracing(false);
  };

  if (loading) return <LoadingState message="Loading reinforcement loops..." />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  const statusColor = { active: C.emerald, designed: C.amber, placeholder: C.dim, broken: C.red };
  const statusLabel = { active: "Active", designed: "Designed", placeholder: "Planned", broken: "Broken" };

  return (
    <div>
      <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:12 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:14,fontWeight:800,color:C.purple }}>Cross-Regulation Reinforcement Loops</div>
          <div style={{ fontSize:9,color:C.muted }}>Each loop shows how one materiality decision cascades across multiple regulations. Traced against live graph data.</div>
        </div>
        <WSIndicator connected={connected} />
      </div>

      <div data-testid="loops-grid" style={{ display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:6,marginBottom:12 }}>
        {loops.map(l => {
          const isSel = selectedLoop === l.id;
          return (
            <div key={l.id} data-testid={`loop-card-${l.id}`}
              onClick={() => { setSelectedLoop(isSel ? null : l.id); setTraceResult(null); }}
              style={{ padding:"12px 14px",borderRadius:8,background:isSel?(l.color||C.purple)+"08":C.card,border:`1px solid ${isSel?(l.color||C.purple)+"44":C.border}`,cursor:"pointer",transition:"all .2s" }}>
              <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:6 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:11,fontWeight:700,color:l.color||C.purple }}>{l.name}</div>
                  <div style={{ fontSize:8,color:C.muted }}>Source: {l.source_topics?.join(", ") || l.esrs_topics?.join(", ") || "N/A"} | {l.step_count || l.link_count || 0} steps</div>
                </div>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontSize:16,fontWeight:800,color:l.color||C.purple }}>{l.health_pct ?? l.completion_pct ?? "?"}%</div>
                  <div style={{ fontSize:7,color:C.dim }}>health</div>
                </div>
              </div>
              <Bar v={l.health_pct || l.completion_pct || 0} mx={100} cl={l.color||C.purple} h={3} />
              <div style={{ display:"flex",gap:3,marginTop:6 }}>
                <Pl color={C.emerald} small>{l.active ? "Active" : "Designed"}</Pl>
                <Pl color={C.amber} small>{l.step_count || 0} steps</Pl>
              </div>
            </div>
          );
        })}
      </div>

      {loop && (
        <div style={{ padding:"14px 16px",borderRadius:8,background:C.surface,border:`1px solid ${loop.color||C.purple}33` }}>
          <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:10 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13,fontWeight:800,color:loop.color||C.purple }}>{loop.name} - Regulation Chain</div>
              <div style={{ fontSize:9,color:C.muted }}>
                When {loop.source_topics?.join("/") || loop.esrs_topics?.join("/") || "?"} is material then {loop.step_count || 0} regulatory workstreams activate.
              </div>
            </div>
            <button data-testid="trace-btn" onClick={() => runTrace(loop.id)} disabled={tracing}
              style={{ padding:"5px 14px",borderRadius:5,border:`1px solid ${C.cyan}44`,background:C.cyan+"12",color:C.cyan,fontSize:9,fontWeight:700,cursor:tracing?"wait":"pointer" }}>
              {tracing ? "Tracing..." : "Trace Against Live Graph"}
            </button>
          </div>

          <div style={{ position:"relative",paddingLeft:20 }}>
            <div style={{ position:"absolute",left:9,top:12,bottom:12,width:2,background:(loop.color||C.purple)+"33",borderRadius:99 }} />
            {(traceResult?.steps || loop.steps || []).map((link, i) => {
              const status = link.status || "placeholder";
              const sc = statusColor[status] || C.dim;
              return (
                <div key={i} style={{ position:"relative",marginBottom:6,paddingLeft:16 }}>
                  <div style={{ position:"absolute",left:-11,top:8,width:10,height:10,borderRadius:"50%",background:sc+"22",border:`2px solid ${sc}`,zIndex:1 }} />
                  <div style={{ padding:"8px 12px",borderRadius:6,background:C.card,border:`1px solid ${sc}33` }}>
                    <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:2 }}>
                      <span style={{ fontSize:10,color:C.text }}>
                        <span style={{ fontWeight:600 }}>{link.from_node || link.source || link.from}</span>
                        <span style={{ color:loop.color||C.purple,margin:"0 5px" }}>-&gt;</span>
                        <span style={{ fontWeight:600 }}>{link.to_node || link.target || link.to}</span>
                      </span>
                      {link.framework && <Pl color={loop.color||C.purple} small>{link.framework}</Pl>}
                      <Pl color={sc} small>{statusLabel[status]}</Pl>
                    </div>
                    <div style={{ fontSize:8,color:C.muted }}>{link.description || link.desc || ""}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {loop.switching_cost && (
            <div style={{ marginTop:10,padding:"8px 12px",borderRadius:6,background:(loop.color||C.purple)+"06",border:`1px solid ${loop.color||C.purple}18`,textAlign:"center" }}>
              <div style={{ fontSize:9,fontWeight:700,color:loop.color||C.purple }}>SWITCHING COST: {loop.switching_cost.level || "HIGH"}</div>
              <div style={{ fontSize:8,color:C.text }}>{loop.switching_cost.description}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// TAB 8: CASCADE SIMULATOR
// ═══════════════════════════════════════════════════════════════════

export function CascadeSimulatorTab() {
  const [selectedIRO, setSelectedIRO] = useState(null);
  const [proposedVerdict, setProposedVerdict] = useState("not_material");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const nav = useModuleNav();

  const { data: iroData, loading: iroLoading, error: iroError, refetch: refetchIROs } = useFetch("/graph/materiality/iros");

  const { lastEvent, connected } = useGraphWS();
  useEffect(() => {
    if (lastEvent?.type === "materiality.verdict.changed") { refetchIROs(); setPreview(null); }
  }, [lastEvent, refetchIROs]);

  const iros = (iroData?.iros || []).map(i => i.iro || i);
  const currentIRO = iros.find(i => i.id === selectedIRO || i.esrs_topic === selectedIRO);

  // Auto-select first IRO
  useEffect(() => {
    if (iros.length > 0 && !selectedIRO) setSelectedIRO(iros[0].id);
  }, [iros, selectedIRO]);

  const isNoChange = currentIRO?.verdict === proposedVerdict;

  const runPreview = async () => {
    setPreviewing(true); setPreviewError(null); setPreview(null);
    const r = await apiFetch("/graph/cascade-simulator/preview", {
      method: "POST", body: { iro_id: selectedIRO, proposed_verdict: proposedVerdict },
    });
    if (r?._error) setPreviewError(r); else setPreview(r);
    setPreviewing(false);
  };

  // Bulk mode
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkChanges, setBulkChanges] = useState({});
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkPreviewing, setBulkPreviewing] = useState(false);

  const runBulkPreview = async () => {
    setBulkPreviewing(true);
    const changes = Object.entries(bulkChanges).filter(([id,v]) => {
      const iro = iros.find(i => i.id === id);
      return iro && iro.verdict !== v;
    }).map(([id, verdict]) => ({ iro_id: id, proposed_verdict: verdict }));
    const r = await apiFetch("/graph/cascade-simulator/bulk-preview", { method: "POST", body: { changes } });
    if (!r?._error) setBulkPreview(r);
    setBulkPreviewing(false);
  };

  if (iroLoading) return <LoadingState message="Loading current IRO states..." />;
  if (iroError) return <ErrorState error={iroError} onRetry={refetchIROs} />;

  return (
    <div>
      <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:12 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:14,fontWeight:800,color:C.cyan }}>Cascade Simulator</div>
          <div style={{ fontSize:9,color:C.muted }}>Preview downstream effects of materiality changes. Read-only - no data modified until you commit.</div>
        </div>
        <WSIndicator connected={connected} />
        <button data-testid="bulk-mode-btn" onClick={() => { setBulkMode(!bulkMode); setPreview(null); setBulkPreview(null); }}
          style={{ padding:"4px 10px",borderRadius:5,border:`1px solid ${bulkMode?C.purple:C.border}`,background:bulkMode?C.purple+"15":"transparent",color:bulkMode?C.purple:C.muted,fontSize:9,fontWeight:600,cursor:"pointer" }}>
          {bulkMode ? "Single Mode" : "Bulk Mode"}
        </button>
      </div>

      {!bulkMode && (
        <div data-testid="single-preview-form" style={{ padding:"12px 14px",borderRadius:8,background:C.surface,border:`1px solid ${C.cyan}22`,marginBottom:10 }}>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr auto",gap:10,alignItems:"end" }}>
            <div>
              <div style={{ fontSize:8,color:C.muted,marginBottom:3 }}>Select IRO (current state from graph)</div>
              <select data-testid="iro-select" value={selectedIRO || ""} onChange={e => { setSelectedIRO(e.target.value); setPreview(null); }}
                style={{ width:"100%",padding:"7px 10px",borderRadius:5,border:`1px solid ${C.border}`,background:C.card,color:C.text,fontSize:10 }}>
                {iros.map(i => <option key={i.id} value={i.id}>{i.esrs_topic || i.id}: {i.name} ({i.verdict})</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:8,color:C.muted,marginBottom:3 }}>Proposed Verdict</div>
              <div style={{ display:"flex",gap:4 }}>
                {["material","not_material"].map(v => (
                  <button key={v} data-testid={`verdict-${v}`} onClick={() => { setProposedVerdict(v); setPreview(null); }}
                    style={{ flex:1,padding:"7px",borderRadius:5,border:`1px solid ${proposedVerdict===v?(v==="material"?C.emerald:C.red):C.border}44`,background:proposedVerdict===v?(v==="material"?C.emerald:C.red)+"15":"transparent",color:proposedVerdict===v?(v==="material"?C.emerald:C.red):C.muted,fontSize:10,fontWeight:600,cursor:"pointer" }}>
                    {v==="material"?"Material":"Not Material"}
                  </button>
                ))}
              </div>
            </div>
            <button data-testid="preview-cascade-btn" onClick={runPreview} disabled={previewing||isNoChange}
              style={{ padding:"7px 20px",borderRadius:5,border:"none",background:isNoChange?C.dim:C.cyan,color:C.bg,fontSize:10,fontWeight:800,cursor:isNoChange?"not-allowed":"pointer",height:34 }}>
              {previewing?"Simulating...":isNoChange?"No Change":"Preview Cascade"}
            </button>
          </div>
          {isNoChange && <div style={{ fontSize:8,color:C.amber,marginTop:6 }}>Selected IRO is already {proposedVerdict}. Choose a different verdict.</div>}
        </div>
      )}

      {bulkMode && (
        <div data-testid="bulk-preview-form" style={{ padding:"12px 14px",borderRadius:8,background:C.surface,border:`1px solid ${C.purple}22`,marginBottom:10 }}>
          <div style={{ fontSize:9,fontWeight:700,color:C.purple,marginBottom:6 }}>BULK PREVIEW - Change multiple IROs at once</div>
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:4,marginBottom:8 }}>
            {iros.map(i => {
              const changed = bulkChanges[i.id];
              const isChanged = changed && changed !== i.verdict;
              return (
                <div key={i.id} style={{ padding:"6px 8px",borderRadius:5,background:C.card,border:`1px solid ${isChanged?C.purple:C.border}33`,fontSize:9 }}>
                  <div style={{ display:"flex",alignItems:"center",gap:4,marginBottom:3 }}>
                    <span style={{ fontWeight:600,color:C.text }}>{i.esrs_topic || i.id}</span>
                    <Pl color={i.verdict==="material"?C.emerald:C.dim} small>{i.verdict==="material"?"MAT":"NOT"}</Pl>
                  </div>
                  <select value={changed || i.verdict} onChange={e => setBulkChanges(prev => ({...prev,[i.id]:e.target.value}))}
                    style={{ width:"100%",padding:"3px",borderRadius:3,border:`1px solid ${C.border}`,background:C.surface,color:C.text,fontSize:8 }}>
                    <option value="material">Material</option>
                    <option value="not_material">Not Material</option>
                  </select>
                </div>
              );
            })}
          </div>
          <button data-testid="bulk-preview-btn" onClick={runBulkPreview} disabled={bulkPreviewing}
            style={{ padding:"6px 16px",borderRadius:5,border:"none",background:Object.keys(bulkChanges).length?C.purple:C.dim,color:C.bg,fontSize:10,fontWeight:700,cursor:"pointer" }}>
            {bulkPreviewing?"Simulating...":"Preview Changes"}
          </button>
        </div>
      )}

      {previewError && <ErrorState error={previewError} />}

      {/* Single Preview Results */}
      {preview && !bulkMode && (
        <div data-testid="preview-results">
          <div style={{ padding:"10px 14px",borderRadius:8,background:proposedVerdict==="material"?C.emerald+"06":C.red+"06",border:`1px solid ${proposedVerdict==="material"?C.emerald:C.red}22`,marginBottom:8 }}>
            <div style={{ display:"flex",alignItems:"center",gap:8 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12,fontWeight:800,color:C.text }}>{preview.iro?.name || selectedIRO}</div>
                <div style={{ fontSize:9,color:C.muted }}>{preview.iro?.current_verdict} -&gt; <span style={{ fontWeight:700,color:proposedVerdict==="material"?C.emerald:C.red }}>{proposedVerdict}</span></div>
              </div>
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:22,fontWeight:800,color:C.cyan }}>{preview.summary?.total_downstream_effects ?? "?"}</div>
                <div style={{ fontSize:7,color:C.dim }}>downstream effects</div>
              </div>
            </div>
          </div>

          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:6,marginBottom:8 }}>
            {preview.datapoints && (
              <div style={{ padding:"10px 12px",borderRadius:7,background:C.card,border:`1px solid ${C.blue}18` }}>
                <div style={{ fontSize:9,fontWeight:700,color:C.blue,marginBottom:6 }}>DATAPOINTS</div>
                <div style={{ fontSize:16,fontWeight:800,color:C.blue,marginBottom:4 }}>
                  {(preview.datapoints.newly_activated || 0) > 0 ? `+${preview.datapoints.newly_activated} activated` : `${preview.datapoints.newly_dormant || 0} dormant`}
                </div>
                {(preview.datapoints.changes || []).slice(0,5).map((dp,i) => (
                  <div key={i} style={{ display:"flex",alignItems:"center",gap:4,padding:"3px 6px",borderRadius:3,background:C.surface,marginBottom:2,fontSize:8 }}>
                    <span style={{ color:dp.proposed_activation==="required"?C.emerald:C.dim }}>*</span>
                    <span style={{ color:C.text,flex:1 }}>{dp.name || dp.datapoint_id}</span>
                    <span style={{ color:C.dim }}>{dp.current_activation}-&gt;{dp.proposed_activation}</span>
                  </div>
                ))}
              </div>
            )}
            {preview.esrs_requirements && (
              <div style={{ padding:"10px 12px",borderRadius:7,background:C.card,border:`1px solid ${C.purple}18` }}>
                <div style={{ fontSize:9,fontWeight:700,color:C.purple,marginBottom:6 }}>ESRS REQUIREMENTS</div>
                <div style={{ fontSize:16,fontWeight:800,color:C.purple,marginBottom:4 }}>{preview.esrs_requirements.count} {preview.esrs_requirements.proposed_status}</div>
                {(preview.esrs_requirements.changes || []).slice(0,5).map((er,i) => (
                  <div key={i} style={{ display:"flex",alignItems:"center",gap:4,padding:"3px 6px",borderRadius:3,background:C.surface,marginBottom:2,fontSize:8 }}>
                    <Pl color={C.purple} small>{er.code}</Pl>
                    <span style={{ color:C.muted }}>{er.label}</span>
                  </div>
                ))}
              </div>
            )}
            {preview.fw_requirements && (
              <div style={{ padding:"10px 12px",borderRadius:7,background:C.card,border:`1px solid ${C.teal}18` }}>
                <div style={{ fontSize:9,fontWeight:700,color:C.teal,marginBottom:6 }}>FRAMEWORKS</div>
                <div style={{ fontSize:16,fontWeight:800,color:C.teal,marginBottom:4 }}>{preview.fw_requirements.count} affected</div>
                {(preview.fw_requirements.changes || []).map((fw,i) => (
                  <div key={i} style={{ display:"flex",alignItems:"center",gap:4,padding:"3px 6px",borderRadius:3,background:C.surface,marginBottom:2,fontSize:8 }}>
                    <Pl color={C.teal} small>{fw.framework}</Pl>
                    <span style={{ color:C.text }}>{fw.requirement}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:8 }}>
            {preview.csddd_scope && (
              <div style={{ padding:"8px 12px",borderRadius:6,background:C.card,border:`1px solid ${C.red}18` }}>
                <div style={{ fontSize:9,fontWeight:700,color:C.red,marginBottom:3 }}>CSDDD SCOPE</div>
                <div style={{ fontSize:9,color:C.text }}>{preview.csddd_scope.detail}</div>
              </div>
            )}
            {preview.export_readiness_delta && (
              <div style={{ padding:"8px 12px",borderRadius:6,background:C.card,border:`1px solid ${C.amber}18` }}>
                <div style={{ fontSize:9,fontWeight:700,color:C.amber,marginBottom:3 }}>EXPORT READINESS</div>
                <div style={{ fontSize:9,color:C.text }}>{preview.export_readiness_delta.reason}</div>
              </div>
            )}
          </div>

          {preview.summary?.recommendation && (
            <div style={{ padding:"8px 12px",borderRadius:6,background:C.cyan+"06",border:`1px solid ${C.cyan}18`,fontSize:9,color:C.text }}>
              <span style={{ fontWeight:700,color:C.cyan }}>Recommendation: </span>{preview.summary.recommendation}
            </div>
          )}

          <div style={{ padding:"8px 12px",borderRadius:6,background:C.cyan+"06",border:`1px solid ${C.cyan}18`,marginTop:6,display:"flex",alignItems:"center",gap:8 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:9,color:C.cyan,fontWeight:600 }}>PREVIEW ONLY - No data modified</div>
            </div>
            <button onClick={() => nav.toMateriality()} style={{ padding:"6px 14px",borderRadius:5,border:`1px solid ${C.emerald}44`,background:C.emerald+"15",color:C.emerald,fontSize:9,fontWeight:700,cursor:"pointer" }}>
              Go to Materiality to Commit
            </button>
          </div>
        </div>
      )}

      {bulkPreview && bulkMode && (
        <div data-testid="bulk-results" style={{ padding:"12px 14px",borderRadius:8,background:C.surface,border:`1px solid ${C.purple}22` }}>
          <div style={{ fontSize:11,fontWeight:700,color:C.purple,marginBottom:6 }}>BULK PREVIEW RESULTS</div>
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:6,marginBottom:8 }}>
            {[
              { l:"Total effects",v:bulkPreview.combined_summary?.total_datapoints_activated + bulkPreview.combined_summary?.total_datapoints_deactivated + bulkPreview.combined_summary?.total_esrs_requirements_changed + bulkPreview.combined_summary?.total_fw_requirements_changed,c:C.cyan },
              { l:"DPs activated",v:bulkPreview.combined_summary?.total_datapoints_activated,c:C.emerald },
              { l:"DPs deactivated",v:bulkPreview.combined_summary?.total_datapoints_deactivated,c:C.red },
              { l:"ESRS changed",v:bulkPreview.combined_summary?.total_esrs_requirements_changed,c:C.purple },
              { l:"FW changed",v:bulkPreview.combined_summary?.total_fw_requirements_changed,c:C.teal },
            ].map((k,i) => (
              <div key={i} style={{ padding:"8px",borderRadius:5,background:C.card,textAlign:"center" }}>
                <div style={{ fontSize:18,fontWeight:800,color:k.c }}>{k.v ?? "?"}</div>
                <div style={{ fontSize:8,color:C.muted }}>{k.l}</div>
              </div>
            ))}
          </div>
          {(bulkPreview.individual_results || []).map(r => (
            <div key={r.iro?.id} style={{ display:"flex",alignItems:"center",gap:6,padding:"6px 10px",borderRadius:5,background:C.card,marginBottom:2,fontSize:9 }}>
              <span style={{ fontWeight:600,color:C.text }}>{r.iro?.esrs_topic || r.iro?.id}</span>
              <span style={{ color:C.dim }}>-&gt;</span>
              <Pl color={r.iro?.proposed_verdict==="material"?C.emerald:C.red} small>{r.iro?.proposed_verdict}</Pl>
              <span style={{ marginLeft:"auto",color:C.cyan }}>{r.summary?.total_downstream_effects || 0} effects</span>
            </div>
          ))}
        </div>
      )}

      {!preview && !bulkPreview && !previewing && !bulkPreviewing && (
        <EmptyState title="Select an IRO, choose a new verdict, and preview the cascade"
          subtitle="See exactly what changes - datapoints, ESRS, frameworks, CSDDD scope - before committing" />
      )}
    </div>
  );
}
